// Mẫu quy trình chuẩn cho thẩm mỹ viện / phòng khám: chọn mẫu khi tạo việc để điền sẵn checklist, chu kỳ lặp, yêu cầu ảnh.
// Nội dung checklist là mẫu tham khảo — chuyên môn cơ sở rà lại theo quy trình nội bộ trước khi áp dụng.
import type { Repeat } from './data';

export interface TaskTemplate {
  id: string;
  group: string;
  name: string;
  departmentId: string;
  description: string;
  checklist: string[];
  repeat?: Repeat;
  dueDays: number; // hạn chót = ngày bắt đầu + dueDays
  important?: boolean;
  urgent?: boolean;
  needReview: boolean;
  needProof?: boolean;
}

export const TEMPLATE_GROUPS = ['An toàn người bệnh', 'Kiểm soát nhiễm khuẩn', 'Thuốc – vật tư – thiết bị', 'Chăm sóc khách hàng', 'Pháp lý – hồ sơ'];

export const TEMPLATES: TaskTemplate[] = [
  {
    id: 'tpl-phanve', group: 'An toàn người bệnh', name: 'Kiểm tra hộp thuốc cấp cứu phản vệ', departmentId: 'd-dd', repeat: 'daily', dueDays: 0,
    important: true, needReview: false, needProof: true,
    description: 'Kiểm tra đầu ca tại từng phòng thủ thuật theo hướng dẫn phòng, chẩn đoán và xử trí phản vệ (Thông tư 51/2017/TT-BYT). Thiếu hoặc hết hạn thì bổ sung ngay trước khi nhận khách.',
    checklist: [
      'Đủ cơ số thuốc theo danh mục hộp chống sốc của cơ sở (Adrenalin 1mg/1ml…)',
      'Không có thuốc hết hạn hoặc cận hạn dưới 1 tháng',
      'Đủ bơm kim tiêm vô khuẩn, dây garô, bông, cồn',
      'Phác đồ xử trí phản vệ dán đúng vị trí, dễ thấy',
      'Ký sổ kiểm tra hộp chống sốc',
    ],
  },
  {
    id: 'tpl-suco', group: 'An toàn người bệnh', name: 'Xử lý sự cố / biến chứng sau thủ thuật', departmentId: 'd-cm', dueDays: 1,
    important: true, urgent: true, needReview: true,
    description: 'Tiếp nhận khi khách báo bất thường sau thủ thuật (sưng đỏ kéo dài, đau nhiều, nghi tắc mạch…). Bác sĩ phụ trách xử trí và báo cáo trong 24 giờ.',
    checklist: [
      'Bác sĩ khám, đánh giá mức độ và xử trí ban đầu',
      'Ghi nhận diễn biến, thuốc đã dùng vào hồ sơ bệnh án',
      'Lập báo cáo sự cố y khoa nội bộ',
      'Hẹn tái khám, giao CSKH theo dõi hằng ngày',
      'Họp rút kinh nghiệm với ê-kíp thực hiện',
    ],
  },
  {
    id: 'tpl-khukhuan', group: 'Kiểm soát nhiễm khuẩn', name: 'Vệ sinh – khử khuẩn phòng thủ thuật cuối ngày', departmentId: 'd-dd', repeat: 'daily', dueDays: 0,
    needReview: false, needProof: true,
    description: 'Thực hiện sau ca cuối, chụp ảnh phòng sau vệ sinh khi báo xong.',
    checklist: [
      'Lau bề mặt giường, xe đẩy, đèn bằng dung dịch khử khuẩn',
      'Thay ga, khăn, vỏ gối dùng một lần',
      'Thu gom dụng cụ dùng lại về khu tiệt khuẩn',
      'Phân loại chất thải lây nhiễm / thông thường đúng thùng',
      'Bật đèn UV hoặc máy lọc khí theo quy định',
    ],
  },
  {
    id: 'tpl-chatthai', group: 'Kiểm soát nhiễm khuẩn', name: 'Bàn giao chất thải y tế', departmentId: 'd-vh', repeat: 'weekly', dueDays: 0,
    needReview: false, needProof: true,
    description: 'Cân, ghi sổ và bàn giao chất thải lây nhiễm cho đơn vị thu gom có chức năng.',
    checklist: [
      'Cân và ghi khối lượng chất thải lây nhiễm, vật sắc nhọn',
      'Kiểm tra túi, hộp đựng đúng màu, đúng nhãn',
      'Ký biên bản / chứng từ bàn giao với đơn vị thu gom',
      'Vệ sinh khu lưu giữ chất thải',
    ],
  },
  {
    id: 'tpl-nhietdo', group: 'Thuốc – vật tư – thiết bị', name: 'Ghi nhiệt độ tủ bảo quản filler, botox, thuốc', departmentId: 'd-kho', repeat: 'daily', dueDays: 0,
    needReview: false, needProof: true,
    description: 'Tủ bảo quản lạnh giữ trong khoảng nhiệt độ nhà sản xuất khuyến cáo (thường 2–8°C). Ngoài ngưỡng phải báo trưởng kho và bác sĩ ngay.',
    checklist: [
      'Ghi nhiệt độ buổi sáng',
      'Ghi nhiệt độ buổi chiều',
      'Không có lọ / hộp để sai vị trí, sai tủ',
      'Báo ngay nếu nhiệt độ ngoài ngưỡng',
    ],
  },
  {
    id: 'tpl-handung', group: 'Thuốc – vật tư – thiết bị', name: 'Rà soát hạn dùng thuốc – vật tư tiêu hao', departmentId: 'd-kho', repeat: 'monthly', dueDays: 3,
    needReview: true,
    description: 'Rà toàn bộ thuốc, filler, botox, vật tư tiêu hao. Hàng cận hạn ưu tiên xuất trước hoặc đề xuất đổi trả.',
    checklist: [
      'Rà hạn dùng từng lô, đối chiếu số lô với phần mềm',
      'Tách riêng hàng cận hạn dưới 3 tháng',
      'Lập danh sách đề xuất huỷ / đổi trả hàng hết hạn',
      'Cập nhật dự trù nhập hàng',
    ],
  },
  {
    id: 'tpl-laser', group: 'Thuốc – vật tư – thiết bị', name: 'Bảo trì – vệ sinh máy laser, HIFU, RF', departmentId: 'd-vh', repeat: 'monthly', dueDays: 2,
    needReview: true, needProof: true,
    description: 'Bảo trì định kỳ thiết bị thẩm mỹ, ghi sổ lý lịch máy. Máy lỗi thì khoá lịch sử dụng và báo hãng.',
    checklist: [
      'Vệ sinh đầu phát, tay cầm, lọc gió',
      'Kiểm tra số shot / tuổi thọ đầu phát còn lại',
      'Chạy thử các chế độ, kiểm tra cảnh báo lỗi',
      'Ghi sổ lý lịch thiết bị',
    ],
  },
  {
    id: 'tpl-hoitham', group: 'Chăm sóc khách hàng', name: 'Gọi hỏi thăm khách sau thủ thuật 24 giờ', departmentId: 'd-cskh', dueDays: 1,
    needReview: false,
    description: 'Gọi khách sau 24 giờ thực hiện. Khách báo bất thường thì chuyển bác sĩ ngay và tạo việc "Xử lý sự cố / biến chứng".',
    checklist: [
      'Hỏi tình trạng sưng, đỏ, đau, bầm',
      'Nhắc lại hướng dẫn chăm sóc tại nhà',
      'Xác nhận lịch tái khám / buổi kế tiếp',
      'Ghi kết quả cuộc gọi vào hồ sơ khách',
    ],
  },
  {
    id: 'tpl-khieunai', group: 'Chăm sóc khách hàng', name: 'Xử lý khiếu nại khách hàng', departmentId: 'd-cskh', dueDays: 2,
    important: true, urgent: true, needReview: true,
    description: 'Phản hồi khách trong 24 giờ, chốt phương án trong 48 giờ.',
    checklist: [
      'Tiếp nhận, ghi rõ nội dung khiếu nại',
      'Xác minh với bộ phận / người thực hiện liên quan',
      'Đề xuất phương án, trình quản lý duyệt',
      'Phản hồi khách và ghi nhận kết quả',
    ],
  },
  {
    id: 'tpl-cchn', group: 'Pháp lý – hồ sơ', name: 'Rà soát chứng chỉ hành nghề và giấy phép', departmentId: 'd-hc', repeat: 'monthly', dueDays: 5,
    important: true, needReview: true,
    description: 'Đảm bảo người hành nghề có chứng chỉ còn hiệu lực, đúng phạm vi chuyên môn; giấy phép hoạt động và giấy phép quảng cáo dịch vụ còn hạn.',
    checklist: [
      'Đối chiếu danh sách bác sĩ, điều dưỡng, KTV với chứng chỉ hành nghề',
      'Kiểm tra phạm vi chuyên môn khớp dịch vụ đang thực hiện',
      'Kiểm tra hạn giấy phép hoạt động, giấy phép quảng cáo',
      'Nhắc gia hạn các giấy tờ hết hạn trong 60 ngày tới',
    ],
  },
];

export const templateOf = (id?: string) => TEMPLATES.find(t => t.id === id);
